import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Play, Stop, Record, Microphone, Export, Sparkle, PaperPlaneRight,
  ShareNetwork, Copy, CaretDown, Metronome, ArrowUUpLeft, Headphones, Repeat,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { StemsDialog } from "./StemsDialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RECORDABLE_TRACK_KINDS } from "./constants";

// Hoisted to module scope so it doesn't create a new object identity every
// render — small perf win for the framer-motion level meter.
const LEVEL_TRANSITION = { duration: 0.05 };

export default function Toolbar({
  project, tracks,
  // recording
  recording, countInBeat, recElapsed, level, downbeatKey,
  recordingKind, setRecordingKind,
  onStartRecord, onStopRecord,
  // playback
  playing, onTogglePlay,
  // ai
  aiOpen, setAiOpen, aiBusy, aiPrompt, setAiPrompt, aiHistory, onAskAi, onClearAi,
  // export
  exporting, onExport, onExportStems,
  // toggles
  onToggleCountIn, onBpmChange, onSetLoopBars,
  // share
  shareOpen, setShareOpen, shareUrl, onToggleShare,
  // monitor while recording
  monitorEnabled, onToggleMonitor, headphoneStatus,
  // undo
  onUndo, undoAvailable,
  // nav
  onBack,
}) {
  const BPM_MIN = 32;
  const BPM_MAX = 220;
  const [editingBpm, setEditingBpm] = useState(false);
  const [bpmDraft, setBpmDraft] = useState("");

  // ---- Stems export dialog (see StemsDialog.jsx) ----
  const stemsDialogRef = useRef(null);

  // Keep the Sound Guy chat pinned to the newest message when the panel opens
  // or a new exchange arrives — older tips stay reachable by scrolling up.
  const aiScrollRef = useRef(null);
  useEffect(() => {
    if (aiOpen && aiScrollRef.current) {
      aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
    }
  }, [aiOpen, aiHistory, aiBusy]);

  // ---- Tap Tempo ----
  // The user hits the TAP button in time with the music; every tap after the
  // first computes an average interval, and on the 4th tap the resulting BPM
  // is committed. Idle > 2 s resets the buffer so a fresh sequence isn't
  // averaged in with the previous one.
  const [tapTimes, setTapTimes] = useState([]);
  const [tapPreview, setTapPreview] = useState(null);
  const tapResetRef = useRef(null);
  useEffect(() => () => {
    if (tapResetRef.current) clearTimeout(tapResetRef.current);
  }, []);

  function tapTempo() {
    const now = performance.now();
    const recent = tapTimes.filter((t) => now - t < 2000);
    const next = [...recent, now];
    let previewBpm = null;
    if (next.length >= 2) {
      const intervals = [];
      for (let i = 1; i < next.length; i++) intervals.push(next[i] - next[i - 1]);
      const avgMs = intervals.reduce((s, x) => s + x, 0) / intervals.length;
      if (avgMs > 0) {
        previewBpm = Math.max(BPM_MIN, Math.min(BPM_MAX, Math.round(60000 / avgMs)));
      }
    }
    if (previewBpm && next.length >= 4) {
      onBpmChange(previewBpm);
      setTapTimes([]);
      setTapPreview(previewBpm);
      if (tapResetRef.current) clearTimeout(tapResetRef.current);
      tapResetRef.current = setTimeout(() => setTapPreview(null), 1600);
      return;
    }
    setTapTimes(next);
    setTapPreview(previewBpm);
    if (tapResetRef.current) clearTimeout(tapResetRef.current);
    tapResetRef.current = setTimeout(() => {
      setTapTimes([]);
      setTapPreview(null);
    }, 2100);
  }

  // Keep the draft in sync with the live project value while not editing.
  useEffect(() => {
    if (!editingBpm) setBpmDraft(String(project?.bpm ?? ""));
  }, [project?.bpm, editingBpm]);

  function commitBpm() {
    const parsed = parseInt(bpmDraft, 10);
    setEditingBpm(false);
    if (!Number.isFinite(parsed)) { setBpmDraft(String(project?.bpm ?? "")); return; }
    const clamped = Math.max(BPM_MIN, Math.min(BPM_MAX, parsed));
    if (clamped !== project?.bpm) onBpmChange(clamped);
    setBpmDraft(String(clamped));
  }

  // Recording-button class + label are derived from `recording` and `countInBeat`.
  // Extracted from nested ternaries for readability.
  let recordBtnClass;
  if (recording) {
    recordBtnClass = "bg-[#E11D48] hover:bg-[#BE123C] text-white";
  } else if (countInBeat > 0) {
    recordBtnClass = "bg-[#A8FF1F] text-[#0A0A0A] opacity-90 cursor-wait";
  } else {
    recordBtnClass = "bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A]";
  }
  let recordBtnBody;
  if (recording) {
    recordBtnBody = <><span className="bb-recording-dot w-2 h-2 bg-white rounded-full mr-2" /> Stop · {recElapsed.toFixed(1)}s</>;
  } else if (countInBeat > 0) {
    recordBtnBody = <><Metronome className="mr-2" size={16} weight="fill" /> Count-in · {countInBeat}/4</>;
  } else {
    recordBtnBody = <><Record className="mr-2" size={16} weight="fill" /> Record</>;
  }

  // Shared menu options (Kind / BPM / Tap / Count-in / Monitor). Rendered twice:
  // inline on desktop, inside a horizontal scroll strip on mobile — the mobile
  // copy suffixes testids with "-m" so every element stays unique.
  const menuOptions = (m) => {
    const tid = (s) => (m ? `${s}-m` : s);
    return (
      <>
        <span className="shrink-0 text-[#9CA0A6] uppercase text-xs tracking-widest font-mono">Kind</span>
        <Select value={recordingKind} onValueChange={setRecordingKind}>
          <SelectTrigger className="shrink-0 bg-[#232427] border-[#34373B] rounded-full h-10 w-[140px] font-mono text-white" data-testid={tid("track-kind-select")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white">
            {RECORDABLE_TRACK_KINDS.map((k) => (
              <SelectItem key={k.v} value={k.v} className="font-mono">
                <span style={{ color: k.c }}>● </span>{k.l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="shrink-0 border-l border-[#34373B] h-6 mx-2" />
        <span className="shrink-0 text-[#9CA0A6] uppercase text-xs tracking-widest font-mono">BPM</span>
        {editingBpm ? (
          <input
            type="number"
            min={BPM_MIN}
            max={BPM_MAX}
            value={bpmDraft}
            autoFocus
            onChange={(e) => setBpmDraft(e.target.value)}
            onBlur={commitBpm}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitBpm(); }
              else if (e.key === "Escape") {
                e.preventDefault();
                setBpmDraft(String(project?.bpm ?? ""));
                setEditingBpm(false);
              }
            }}
            data-testid={tid("bpm-input")}
            className="shrink-0 font-display text-2xl tracking-tighter text-[#A8FF1F] bg-transparent border-b border-[#A8FF1F] outline-none w-16 text-center"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingBpm(true)}
            data-testid={tid("bpm-display")}
            title={`Click to set BPM (${BPM_MIN}–${BPM_MAX})`}
            className="shrink-0 font-display text-2xl tracking-tighter text-[#A8FF1F] cursor-text hover:text-[#94E600] transition-colors"
          >
            {project?.bpm}
          </button>
        )}
        <button
          type="button"
          onClick={tapTempo}
          data-testid={tid("tap-tempo-btn")}
          title="Tap 4 times in rhythm to set BPM by feel"
          className={`shrink-0 whitespace-nowrap bb-pill h-10 px-3 inline-flex items-center gap-1.5 border text-xs font-mono uppercase tracking-widest transition-colors ${
            tapTimes.length > 0 || tapPreview
              ? "bg-[#A8FF1F]/10 border-[#A8FF1F] text-[#A8FF1F]"
              : "bg-[#232427] border-[#34373B] text-[#9CA0A6] hover:text-white"
          }`}
        >
          <Metronome size={14} weight="bold" />
          {tapTimes.length === 0 && !tapPreview && "Tap"}
          {tapTimes.length > 0 && (
            <span data-testid={tid("tap-tempo-progress")}>
              {tapTimes.length}/4{tapPreview ? ` · ${tapPreview}` : ""}
            </span>
          )}
          {tapTimes.length === 0 && tapPreview && (
            <span data-testid={tid("tap-tempo-committed")}>→ {tapPreview}</span>
          )}
        </button>
        <div className="shrink-0 border-l border-[#34373B] h-6 mx-2" />
        <button
          type="button"
          onClick={() => onToggleCountIn(!project?.count_in)}
          data-testid={tid("count-in-toggle")}
          className={`shrink-0 bb-pill h-10 w-10 inline-flex items-center justify-center border transition-colors ${
            project?.count_in
              ? "bg-[#A8FF1F]/10 border-[#A8FF1F] text-[#A8FF1F]"
              : "bg-[#232427] border-[#34373B] text-[#9CA0A6] hover:text-white"
          }`}
          title={`Count-in ${project?.count_in ? "ON — 4 clicks before recording" : "OFF"} — play 4 quarter-note clicks before recording so you can lock in the timing`}
          aria-label={`Count-in ${project?.count_in ? "on" : "off"}`}
        >
          <Metronome size={16} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => onSetLoopBars((project?.loop_bars || 4) === 4 ? 8 : 4)}
          data-testid={tid("loop-bars-toggle")}
          className={`shrink-0 whitespace-nowrap bb-pill h-10 px-3 inline-flex items-center gap-2 border text-xs font-mono uppercase tracking-widest transition-colors ${
            (project?.loop_bars || 4) === 8
              ? "bg-[#A8FF1F]/10 border-[#A8FF1F] text-[#A8FF1F]"
              : "bg-[#232427] border-[#34373B] text-[#9CA0A6] hover:text-white"
          }`}
          title="Loop window length — tap to switch between 4 and 8 bars. New recordings and playback loop over this many bars."
        >
          <Repeat size={14} weight="bold" /> {project?.loop_bars || 4} bars
        </button>
        <button
          type="button"
          onClick={() => onToggleMonitor(!monitorEnabled)}
          disabled={headphoneStatus?.hasSpeakers && !headphoneStatus?.hasHeadphones}
          data-testid={tid("monitor-toggle")}
          className={`shrink-0 bb-pill h-10 w-10 inline-flex items-center justify-center border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            monitorEnabled
              ? "bg-[#A8FF1F]/10 border-[#A8FF1F] text-[#A8FF1F]"
              : "bg-[#232427] border-[#34373B] text-[#9CA0A6] hover:text-white"
          }`}
          title={(() => {
            const state = `Monitor ${monitorEnabled ? "ON" : "OFF"}. `;
            if (headphoneStatus?.hasHeadphones) {
              return `${state}Headphones detected (${headphoneStatus.sample?.[0] || "audio device"}). Monitor plays existing tracks while you overdub.`;
            }
            if (headphoneStatus?.hasSpeakers) {
              return `${state}Speakers detected — monitoring is disabled to prevent mic feedback. Plug in headphones to enable.`;
            }
            return `${state}Monitor plays existing tracks through your output while you record. Only turn on with headphones — otherwise the mic will pick up the mix.`;
          })()}
          aria-label={`Monitor ${monitorEnabled ? "on" : "off"}`}
        >
          <Headphones size={16} weight="bold" />
        </button>
      </>
    );
  };

  return (
    <>
      <header className="border-b border-[#34373B] sticky top-0 z-20 bg-[#1A1B1D]">
        <div className="px-3 md:px-4 lg:px-8 h-16 flex items-center gap-2 md:gap-3 lg:gap-5">
          <button
            onClick={onBack}
            className="text-[#9CA0A6] hover:text-white p-2 -ml-2 rounded-full hover:bg-white/5 shrink-0"
            data-testid="back-to-dashboard-btn"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="hidden sm:flex items-center gap-3 border-r border-[#34373B] pr-5">
            <div className="font-display text-xl tracking-tighter truncate max-w-[200px]" data-testid="project-name">
              {project?.name}
            </div>
          </div>
          {/* Mobile-only compact title (no divider so we save horizontal space). */}
          <div className="sm:hidden font-display text-lg tracking-tighter truncate max-w-[110px]" data-testid="project-name-mobile">
            {project?.name}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              {downbeatKey > 0 && (
                <span
                  key={downbeatKey}
                  aria-hidden
                  data-testid="downbeat-flash"
                  data-downbeat={downbeatKey}
                  className="bb-downbeat-flash pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#A8FF1F]"
                />
              )}
              <Button
                data-testid={recording ? "stop-record-btn" : "record-btn"}
                onClick={recording ? onStopRecord : onStartRecord}
                disabled={!recording && countInBeat > 0}
                className={`bb-pill h-10 px-3 md:px-5 font-semibold md:min-w-[150px] ${recordBtnClass}`}
              >
                {recordBtnBody}
              </Button>
            </div>
            <Button
              data-testid={playing ? "stop-btn" : "play-btn"}
              onClick={onTogglePlay}
              aria-label={playing ? "Stop" : "Play"}
              className="bb-pill h-10 px-3 md:px-5 border border-[#34373B] bg-[#232427] hover:bg-[#2D2F33] text-white font-semibold"
            >
              {playing ? (
                <>
                  <Stop className="md:mr-2" size={16} weight="fill" /> <span className="hidden md:inline">Stop</span>
                </>
              ) : (
                <>
                  <Play className="md:mr-2" size={16} weight="fill" /> <span className="hidden md:inline">Play</span>
                </>
              )}
            </Button>
          </div>

          <div className="hidden md:flex items-center gap-2 ml-auto border-l border-[#34373B] pl-5">
            {menuOptions(false)}
          </div>

          <div className="flex items-center gap-2 ml-auto md:ml-0">
            <Button
              onClick={onUndo}
              disabled={!undoAvailable}
              data-testid="undo-btn"
              aria-label="Undo last edit"
              title="Undo last edit (Ctrl/Cmd+Z)"
              className="bb-pill h-10 w-10 p-0 grid place-items-center bg-[#232427] hover:bg-[#2D2F33] border border-[#34373B] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowUUpLeft size={16} weight="bold" />
            </Button>
            <Button
              onClick={() => setShareOpen(true)}
              data-testid="open-share-btn"
              aria-label="Share"
              title="Share project"
              className="bb-pill h-10 w-10 p-0 grid place-items-center bg-[#232427] hover:bg-[#2D2F33] border border-[#34373B] text-white font-semibold"
            >
              <ShareNetwork size={16} weight="bold" />
            </Button>
            <Sheet open={aiOpen} onOpenChange={setAiOpen}>
              <SheetTrigger asChild>
                <Button
                  data-testid="open-ai-btn"
                  aria-label="Sound Guy"
                  title="Sound Guy — in-app assistant"
                  className="bb-pill h-10 w-10 p-0 grid place-items-center bg-white text-[#0A0A0A] hover:bg-[#E2E3E5] font-semibold"
                >
                  <Sparkle size={16} weight="fill" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bb-editor border-l border-[#34373B] text-white p-0 w-full sm:w-[420px] sm:max-w-[480px]">
                <SheetHeader className="p-6 border-b border-[#34373B]">
                  <SheetTitle className="font-display text-2xl tracking-tighter text-white flex items-center gap-2">
                    <Sparkle size={20} weight="fill" className="text-[#A8FF1F]" /> Sound Guy
                    {aiHistory.length > 0 && (
                      <button
                        type="button"
                        onClick={onClearAi}
                        data-testid="ai-clear-btn"
                        title="Clear chat history"
                        className="ml-auto mr-6 text-[10px] font-mono uppercase tracking-widest text-[#9CA0A6] hover:text-white border border-[#34373B] rounded-full px-3 py-1.5 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </SheetTitle>
                  <p className="text-xs text-[#9CA0A6] font-mono uppercase tracking-widest">In-app assistant · saved per project</p>
                </SheetHeader>
                <div className="flex flex-col h-[calc(100vh-100px)]">
                  <div ref={aiScrollRef} className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="ai-history">
                    {aiHistory.length === 0 && (
                      <div className="text-[#9CA0A6] text-sm leading-relaxed">
                        <p className="mb-3">Hey — I'm here to help you drive Beat Box. Ask me:</p>
                        <ul className="list-disc pl-5 space-y-1.5 text-[#B5B8BE]">
                          <li>How does Drumify / Chordify / Instrument work?</li>
                          <li>What's the difference between Sync and Snap?</li>
                          <li>When should I use Auto-tune to key?</li>
                          <li>How do I layer a rhythm and a melody together?</li>
                          <li>Should I turn Monitor on?</li>
                        </ul>
                      </div>
                    )}
                    {aiHistory.map((m) => (
                      <div key={m.id} className={`text-sm ${m.role === "user" ? "text-white" : "text-[#D5D7DA]"}`}>
                        <div className="text-xs uppercase tracking-widest font-mono mb-2 text-[#9CA0A6]">
                          {m.role === "user" ? "YOU" : "SOUND GUY"}
                        </div>
                        <div className="whitespace-pre-wrap leading-relaxed border-l-2 pl-3" style={{ borderColor: m.role === "user" ? "#A8FF1F" : "#60A5FA" }}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                    {aiBusy && <div className="text-[#9CA0A6] text-sm font-mono">thinking…</div>}
                  </div>
                  <div className="p-4 border-t border-[#34373B]">
                    <form onSubmit={(e) => { e.preventDefault(); onAskAi(); }} className="flex gap-2">
                      <Textarea
                        value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={2}
                        placeholder="How do I use Drumify on a rhythm take?"
                        className="bg-[#232427] border-[#34373B] rounded-2xl resize-none font-mono text-sm text-white"
                        data-testid="ai-prompt-input"
                      />
                      <Button type="submit" disabled={aiBusy || !aiPrompt.trim()} data-testid="ai-send-btn" className="rounded-full bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-auto px-4">
                        <PaperPlaneRight size={18} weight="fill" />
                      </Button>
                    </form>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={exporting || tracks.length === 0}
                  data-testid="export-btn"
                  aria-label="Export"
                  title={exporting ? "Rendering export…" : "Export project (WAV / MP3)"}
                  className="bb-pill h-10 px-2 grid grid-flow-col auto-cols-max items-center gap-1 bg-[#232427] hover:bg-[#2D2F33] border border-[#34373B] text-white font-semibold"
                >
                  <Export size={16} weight="bold" className={exporting ? "animate-pulse text-[#A8FF1F]" : ""} />
                  <CaretDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A1B1D] border-[#34373B] rounded-2xl text-white font-mono">
                <DropdownMenuItem onSelect={() => onExport("wav")} data-testid="export-wav-item" className="cursor-pointer">
                  WAV · Lossless
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onExport("mp3")} data-testid="export-mp3-item" className="cursor-pointer">
                  MP3 · 192 kbps
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => stemsDialogRef.current?.open()} data-testid="export-stems-item" className="cursor-pointer">
                  Stems · ZIP (up to 3)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Mobile-only menu strip — scrolls horizontally on its own so users can
            reach every option (Kind, BPM, Tap, Count-in, Monitor) without the
            page moving. */}
        <div
          className="md:hidden flex items-center gap-2 overflow-x-auto px-3 pb-2.5 pt-0.5 overscroll-x-contain"
          data-testid="mobile-toolbar-scroll"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {menuOptions(true)}
        </div>
        {recording && (
          <div className="h-1 bg-[#232427]">
            <motion.div className="h-full bg-[#A8FF1F]" animate={{ width: `${level * 100}%` }} transition={LEVEL_TRANSITION} />
          </div>
        )}
      </header>

      <StemsDialog
        ref={stemsDialogRef}
        project={project}
        tracks={tracks}
        exporting={exporting}
        onExportStems={onExportStems}
      />

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-[#1A1B1D] border border-[#34373B] rounded-3xl text-white" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-tighter flex items-center gap-2">
              <ShareNetwork size={22} weight="bold" /> Share project
            </DialogTitle>
            <DialogDescription className="text-[#9CA0A6] text-sm">
              Make this project public so anyone with the link can play and download your mix.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="flex items-center justify-between p-5 bb-editor-card rounded-2xl">
              <div>
                <div className="font-display text-lg tracking-tight">Public link</div>
                <div className="text-[#9CA0A6] text-sm">Anyone with the link can play & download.</div>
              </div>
              <Switch
                checked={!!project?.is_public}
                onCheckedChange={onToggleShare}
                data-testid="share-toggle"
              />
            </div>
            {project?.is_public && (
              <div className="flex gap-2">
                <Input
                  readOnly value={shareUrl}
                  className="bg-[#232427] border-[#34373B] rounded-full h-12 font-mono text-xs px-5 text-white"
                  data-testid="share-url-input"
                />
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success("Link copied");
                  }}
                  data-testid="copy-share-link-btn"
                  className="bb-pill h-12 px-5 bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A]"
                >
                  <Copy size={16} weight="bold" />
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShareOpen(false)} data-testid="close-share-btn" className="bb-pill bg-[#232427] hover:bg-[#2D2F33] text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Re-export so consumers can stay on the same module for the "Record another layer" CTA icon.
export { Microphone };
